import type {
  SmartNotification,
  NotificationOptimization,
  EngagementPattern,
  TimingPreference,
  NotificationDelivery,
} from '@coffeeplexity/shared';
import { AINotificationType } from '@coffeeplexity/shared';

// Mock smart notifications
export const mockSmartNotifications: SmartNotification[] = [
  {
    id: 'notif_1',
    userId: 'user_123',
    type: AINotificationType.MILESTONE_ACHIEVEMENT,
    title: '🏆 Brewing Milestone Reached!',
    content:
      "Congratulations! You've successfully completed 10 pour-over brews. Your consistency is improving!",
    aiReasoning:
      'Based on your recent brewing activity and skill progression, this achievement recognition will reinforce positive learning behavior and encourage continued practice.',
    optimalTiming: new Date(Date.now() - 30 * 60 * 1000).toISOString(), // 30 minutes ago
    confidence: 0.92,
    personalizationFactors: [
      'Recent brewing frequency increase',
      'Consistent technique improvement',
      'Positive engagement with achievement notifications',
    ],
    priority: 'high',
    channel: 'push',
    scheduledFor: new Date(Date.now() - 30 * 60 * 1000).toISOString(),
    expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
    metadata: {
      achievementId: 'pour_over_10',
      actionRequired: 'View your brewing progress',
      celebrationType: 'milestone',
    },
  },
  {
    id: 'notif_2',
    userId: 'user_123',
    type: AINotificationType.NEW_RECOMMENDATION,
    title: '💡 Perfect Recipe for Your Skill Level',
    content:
      'Try this Ethiopian Yirgacheffe recipe - it matches your current pour-over skills and taste preferences.',
    aiReasoning:
      'Your recent successful brews with medium roasts and your preference for bright, fruity flavors make this Ethiopian single-origin an ideal next step in your coffee journey.',
    optimalTiming: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(), // 2 hours ago
    confidence: 0.87,
    personalizationFactors: [
      'Preference for bright, fruity flavors',
      'Success with medium roast profiles',
      'Current pour-over skill level',
      'Morning brewing routine',
    ],
    priority: 'medium',
    channel: 'push',
    scheduledFor: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
    expiresAt: new Date(Date.now() + 48 * 60 * 60 * 1000).toISOString(),
    metadata: {
      contentId: 'recipe_ethiopian_yirgacheffe',
      contentType: 'recipe',
      skillAlignment: 0.85,
    },
  },
  {
    id: 'notif_3',
    userId: 'user_123',
    type: AINotificationType.STREAK_REMINDER,
    title: '🔥 Keep Your Streak Alive!',
    content:
      "You're on a 7-day brewing streak! Don't break it now - try a quick 5-minute brew.",
    aiReasoning:
      'Your 7-day streak shows strong commitment. A gentle reminder at your typical brewing time will help maintain momentum without pressure.',
    optimalTiming: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(), // 4 hours ago
    confidence: 0.78,
    personalizationFactors: [
      'Current 7-day streak',
      'Historical streak maintenance success',
      'Preference for morning brewing',
      'Positive response to streak reminders',
    ],
    priority: 'medium',
    channel: 'push',
    scheduledFor: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
    expiresAt: new Date(Date.now() + 12 * 60 * 60 * 1000).toISOString(),
    metadata: {
      streakCount: 7,
      streakType: 'daily_brewing',
      quickBrewSuggestion: true,
    },
  },
  {
    id: 'notif_4',
    userId: 'user_123',
    type: AINotificationType.JOURNEY_ADAPTATION,
    title: '🎯 Your Journey Has Been Optimized',
    content:
      "Based on your progress, we've adjusted your learning path to focus more on espresso techniques.",
    aiReasoning:
      "Your rapid mastery of pour-over fundamentals and expressed interest in espresso equipment suggests you're ready for more advanced brewing methods.",
    optimalTiming: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString(), // 6 hours ago
    confidence: 0.83,
    personalizationFactors: [
      'Rapid pour-over skill progression',
      'Interest in espresso equipment',
      'Completion of foundational modules',
      'Preference for challenging content',
    ],
    priority: 'high',
    channel: 'in_app',
    scheduledFor: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString(),
    expiresAt: new Date(Date.now() + 72 * 60 * 60 * 1000).toISOString(),
    metadata: {
      adaptationType: 'skill_focus_shift',
      newFocus: 'espresso_techniques',
      previousFocus: 'pour_over_mastery',
    },
  },
  {
    id: 'notif_5',
    userId: 'user_123',
    type: AINotificationType.CONTENT_SUGGESTION,
    title: '📚 New Video: Espresso Extraction',
    content:
      'Master barista James explains extraction timing - perfect for your current learning stage.',
    aiReasoning:
      'This video content aligns with your recent interest in espresso and your learning style preference for visual demonstrations.',
    optimalTiming: new Date(Date.now() - 8 * 60 * 60 * 1000).toISOString(), // 8 hours ago
    confidence: 0.75,
    personalizationFactors: [
      'Visual learning preference',
      'Interest in espresso techniques',
      'Positive engagement with video content',
      'Evening learning sessions',
    ],
    priority: 'low',
    channel: 'in_app',
    scheduledFor: new Date(Date.now() - 8 * 60 * 60 * 1000).toISOString(),
    expiresAt: new Date(Date.now() + 96 * 60 * 60 * 1000).toISOString(),
    metadata: {
      contentId: 'video_espresso_extraction_timing',
      contentType: 'video',
      creatorId: 'james_hoffmann',
      duration: 12,
    },
  },
];

// Mock notification optimization settings
export const mockNotificationOptimization: NotificationOptimization = {
  userId: 'user_123',
  optimalTimes: ['08:00', '12:30', '19:00'],
  frequencyLimit: 5,
  engagementThreshold: 0.35,
  adaptationRate: 0.65,
  quietHours: {
    enabled: true,
    startTime: '22:00',
    endTime: '07:00',
    timezone: 'America/New_York',
  },
  channelPreferences: [
    {
      channel: 'push',
      enabled: true,
      priority: 1,
      types: ['milestone_achievement', 'streak_reminder', 'new_recommendation'],
    },
    {
      channel: 'in_app',
      enabled: true,
      priority: 2,
      types: ['journey_adaptation', 'content_suggestion', 'social_activity'],
    },
    {
      channel: 'email',
      enabled: false,
      priority: 3,
      types: ['weekly_summary', 'important_updates'],
    },
  ],
  lastOptimized: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
};

// Mock engagement patterns
export const mockEngagementPatterns: EngagementPattern[] = [
  {
    id: 'pattern_1',
    userId: 'user_123',
    patternType: 'time_based',
    pattern: {
      peakHours: [8, 12, 19],
      lowHours: [14, 15, 16, 22, 23, 0, 1, 2, 3, 4, 5, 6],
      weekdayVsWeekend: { weekday: 0.75, weekend: 0.45 },
    },
    strength: 0.82,
    lastObserved: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
    predictiveValue: 0.78,
  },
  {
    id: 'pattern_2',
    userId: 'user_123',
    patternType: 'content_based',
    pattern: {
      preferredTypes: ['milestone_achievement', 'new_recommendation'],
      avoidedTypes: ['re_engagement'],
      engagementByType: {
        milestone_achievement: 0.92,
        new_recommendation: 0.87,
        streak_reminder: 0.65,
        content_suggestion: 0.58,
        journey_adaptation: 0.71,
      },
    },
    strength: 0.74,
    lastObserved: new Date(Date.now() - 1 * 60 * 60 * 1000).toISOString(),
    predictiveValue: 0.69,
  },
  {
    id: 'pattern_3',
    userId: 'user_123',
    patternType: 'frequency_based',
    pattern: {
      optimalFrequency: 4,
      toleranceRange: [3, 6],
      fatigueThreshold: 8,
      recoveryTime: 12,
    },
    strength: 0.67,
    lastObserved: new Date(Date.now() - 30 * 60 * 1000).toISOString(),
    predictiveValue: 0.63,
  },
];

// Mock timing preferences
export const mockTimingPreferences: TimingPreference[] = [
  {
    userId: 'user_123',
    dayOfWeek: 1, // Monday
    hourOfDay: 8,
    engagementRate: 0.89,
    sampleSize: 23,
    confidence: 0.85,
    lastUpdated: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    userId: 'user_123',
    dayOfWeek: 1,
    hourOfDay: 12,
    engagementRate: 0.76,
    sampleSize: 18,
    confidence: 0.78,
    lastUpdated: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    userId: 'user_123',
    dayOfWeek: 1,
    hourOfDay: 19,
    engagementRate: 0.82,
    sampleSize: 21,
    confidence: 0.81,
    lastUpdated: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    userId: 'user_123',
    dayOfWeek: 2, // Tuesday
    hourOfDay: 8,
    engagementRate: 0.91,
    sampleSize: 25,
    confidence: 0.87,
    lastUpdated: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    userId: 'user_123',
    dayOfWeek: 6, // Saturday
    hourOfDay: 10,
    engagementRate: 0.45,
    sampleSize: 12,
    confidence: 0.52,
    lastUpdated: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
  },
];

// Mock delivery history
export const mockDeliveryHistory: NotificationDelivery[] = [
  {
    id: 'delivery_1',
    userId: 'user_123',
    notificationId: 'notif_1',
    channel: 'push',
    deliveredAt: new Date(Date.now() - 30 * 60 * 1000).toISOString(),
    openedAt: new Date(Date.now() - 25 * 60 * 1000).toISOString(),
    clickedAt: new Date(Date.now() - 24 * 60 * 1000).toISOString(),
    dismissed: false,
    engagementScore: 0.95,
    context: {
      timeOfDay: 8,
      dayOfWeek: 1,
      appState: 'active',
      deviceType: 'mobile',
    },
  },
  {
    id: 'delivery_2',
    userId: 'user_123',
    notificationId: 'notif_2',
    channel: 'push',
    deliveredAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
    openedAt: new Date(Date.now() - 115 * 60 * 1000).toISOString(),
    dismissed: false,
    engagementScore: 0.72,
    context: {
      timeOfDay: 12,
      dayOfWeek: 1,
      appState: 'background',
      deviceType: 'mobile',
    },
  },
  {
    id: 'delivery_3',
    userId: 'user_123',
    notificationId: 'notif_3',
    channel: 'push',
    deliveredAt: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
    dismissed: true,
    engagementScore: 0.15,
    context: {
      timeOfDay: 14,
      dayOfWeek: 1,
      appState: 'inactive',
      deviceType: 'mobile',
    },
  },
];

// Utility function to generate mock notifications for testing
export const generateMockNotification = (
  type: AINotificationType,
  priority: 'high' | 'medium' | 'low' = 'medium'
): SmartNotification => {
  const baseNotification = {
    id: `notif_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    userId: 'user_123',
    type,
    priority,
    channel: 'push' as const,
    scheduledFor: new Date().toISOString(),
    expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
    confidence: 0.7 + Math.random() * 0.3,
    personalizationFactors: [
      'User behavior analysis',
      'Historical engagement patterns',
      'Current learning progress',
    ],
    metadata: {},
  };

  switch (type) {
    case AINotificationType.MILESTONE_ACHIEVEMENT:
      return {
        ...baseNotification,
        title: '🏆 New Achievement Unlocked!',
        content: "You've reached a new milestone in your coffee journey!",
        aiReasoning:
          'Achievement notifications have shown high engagement rates for this user.',
        optimalTiming: new Date().toISOString(),
      };

    case AINotificationType.STREAK_REMINDER:
      return {
        ...baseNotification,
        title: "🔥 Don't Break Your Streak!",
        content: 'Keep your brewing momentum going with a quick session.',
        aiReasoning:
          "Streak reminders are most effective during the user's typical brewing window.",
        optimalTiming: new Date().toISOString(),
      };

    case AINotificationType.NEW_RECOMMENDATION:
      return {
        ...baseNotification,
        title: '💡 New Recipe Recommendation',
        content: 'We found a perfect recipe match for your taste preferences.',
        aiReasoning:
          'Based on recent taste profile updates and successful recipe completions.',
        optimalTiming: new Date().toISOString(),
      };

    default:
      return {
        ...baseNotification,
        title: '📱 Coffee Update',
        content: 'Something new is waiting for you in your coffee journey.',
        aiReasoning: 'General notification based on user activity patterns.',
        optimalTiming: new Date().toISOString(),
      };
  }
};
